import { useCallback } from 'react';
import { doc, collection, serverTimestamp, query, where, updateDoc, writeBatch, getDocs } from 'firebase/firestore';
import { getOrCreateDeviceId, getIpAddress } from '../utils/helpers';


// ✅ NEW IMPORTS for Cloud Function call
import { callable } from "../firebase";


// Helper function to capture device and IP details
const getDeviceInfo = (deviceId, ipAddress) => {
    // NOTE: Real IP detection is tricky client-side; often results in a public-facing IP
    // when run from a non-proxied environment, or relies on headers if run server-side (CF).
    // Here we provide the client's User Agent and a mock deviceId.
    // const deviceId = localStorage.getItem('deviceId') || 'web-default';
    
    // In a browser environment, IP is usually read from the request header in the Cloud Function.
    // However, if the client needs to explicitly send an IP (not recommended for HttpsCallable), 
    // we use a placeholder or rely on a third-party service, but for now, we rely on CF's context 
    // or provide the best available local info.

    return {
        deviceId,
        userAgent: navigator.userAgent,
        // The Cloud Function will grab the IP from the request context, 
        // but we can provide a placeholder or reliance flag here.
        ipAddress, 
    };
};


export const useTradeActions = ({ db, navigate, setModal, profile, tradeInfo, user }) => {
    
    // Helper to call the secure Reclaim Credits Cloud Function (Client side)
    const reclaimExpiredCredits = useCallback(async () => {
    // const handleReclaimCreditsCloudFunction = useCallback(async () => {
        if (!tradeInfo || !profile) return;
        setModal({ title: "Processando...", message: "Devolvendo seus créditos..." });

        try {
            // Call the secure CF with the trade ID
            const reclaimFunction = callable('reclaimExpiredCredits');
            const result = await reclaimFunction({ tradeId: tradeInfo.id });

            setModal({ message: result.data.message || "Troca cancelada e créditos devolvidos com sucesso!" });
            navigate('market');
            
        } catch (error) {
            console.error("Error calling reclaimExpiredCredits CF:", error);
            // HttpsError message translation is handled on the client via error.message
            setModal({ title: "Erro", message: error.message || "Erro ao cancelar a troca." });
        }
    }, [tradeInfo, profile, setModal, navigate]);

    // Generic helper to update the trade document and handle feedback
    const updateTrade = useCallback(async (data, successMessage) => {
        if (!tradeInfo || !profile) return;
        const tradeRef = doc(db, "trades", tradeInfo.id);
        try {
            await updateDoc(tradeRef, { ...data, lastUpdatedBy: profile.id });
            if (successMessage) {
                setModal({ message: successMessage });
            }
        } catch (error) {
            setModal({ title: "Erro", message: `Ação falhou: ${error.message}` });
        }
    }, [tradeInfo, profile, db, setModal]);
    
    // Seller confirms delivery through the confirmDelivery callable (state machine).
    // This ensures sellerDeviceInfo is captured and security rules are enforced.
    const handleConfirmDelivery = useCallback(() => {
        setModal({
            title: "Confirmar Entrega",
            message: "Você confirma que entregou o item ao comprador?",
            actions: [
                { text: "Cancelar", onClick: () => setModal(null) },
                { 
                    text: "Sim, entreguei", 
                    onClick: async () => {
                        if (!tradeInfo || !profile) return;
                        setModal({ title: "Processando...", message: "Registrando entrega e dispositivo origem..." });
                        
                        try {
						    const deviceId = getOrCreateDeviceId();
							const ipAddress = await getIpAddress();

                            const sellerDeviceInfo = getDeviceInfo(deviceId, ipAddress);
                            const confirmFunction = callable('confirmDelivery');
                            
                            const result = await confirmFunction({ 
                                tradeId: tradeInfo.id, 
                                sellerDeviceInfo: sellerDeviceInfo 
                            });

                            setModal({ message: result.data.message || "Você marcou o item como entregue. Comprador notificado." });
                        } catch (error) {
                            console.error("Error calling confirmDelivery:", error);
                            setModal({ title: "Erro", message: error.message || "Erro ao registrar a entrega." });
                        }
                    }
                }
            ]
        });
    }, [setModal, tradeInfo, profile]);

    // ✅ REVISED: This now calls a secure Cloud Function instead of running a client-side batch write.
    const handleConfirmReceipt = useCallback(() => {
        if (!tradeInfo || !profile) return;
        setModal({
            title: "Confirmar Recebimento",
            message: "Esta ação é irreversível e irá transferir os créditos para o vendedor. Confirma?",
            actions: [
                { text: "Cancelar", onClick: () => setModal(null) },
                {
                    text: "Sim, recebi",
                    onClick: async () => {
                        setModal({ title: "Processando...", message: "Finalizando a troca, por favor aguarde." });
                        try {
						    const deviceId = getOrCreateDeviceId();
							const ipAddress = await getIpAddress();
							const buyerDeviceInfo = getDeviceInfo(deviceId, ipAddress);
							
                            const confirmFunction = callable('completeTrade'); // Use the existing 'completeTrade' CF
                            const result = await confirmFunction({ tradeId: tradeInfo.id, buyerDeviceInfo: buyerDeviceInfo });

                            setModal({ message: result.data.message || "Troca concluída com sucesso!" });
                        } catch (error) {
                            console.error("Error calling completeTrade CF:", error);
                            setModal({ title: "Erro", message: error.message || "Erro ao concluir a troca." });
                        }
                    }
                }
            ]
        });
    }, [tradeInfo, profile, setModal]); // Removed 'db' dependencies as it's now using functions

    // ✅ REVISED: Uses the Cloud Function call for security.
    const handleReclaimCredits = useCallback(async () => {
        if (!tradeInfo || !profile) return;
        setModal({
            title: "Reivindicar Créditos",
            message: "Tem certeza de que deseja cancelar esta troca expirada e receber seus créditos de volta?",
            actions: [
                { text: "Não", onClick: () => setModal(null) },
                {
                    text: "Sim, reivindicar",
                    // Calls the secure, server-side function
                    //onClick: handleReclaimCreditsCloudFunction
		    onClick: reclaimExpiredCredits			
                }
            ]
        });
    }, [tradeInfo, profile, setModal, reclaimExpiredCredits]);
    
    // ... (handleOpenDispute and subsequent functions are fine)
    
    // Status changes happen only on the server (trade state machine).
    const handleOpenDispute = useCallback(async (reason) => {
        if (!tradeInfo) return;
        try {
            const { data } = await callable('openDispute')({ tradeId: tradeInfo.id, reason });
            setModal({ message: data.message });
        } catch (error) {
            setModal({ title: "Erro", message: error.message || "Não foi possível abrir a disputa." });
        }
    }, [tradeInfo, setModal]);

    // The trade's chat is created by initiateTrade (id = trade id).
    const handleOpenChat = useCallback(async () => {
        if (!tradeInfo || !profile) return;
        const q = query(collection(db, 'chats'), where("participants", "array-contains", profile.id), where("tradeId", "==", tradeInfo.id)); // rules only allow listing your own chats
        try {
            const querySnapshot = await getDocs(q);
            if (querySnapshot.empty) {
                setModal({ message: "O chat desta troca não está disponível." });
                return;
            }
            navigate(`/chat/${querySnapshot.docs[0].id}`);
        } catch (error) {
            setModal({ message: "Não foi possível abrir o chat." });
        }
    }, [tradeInfo, profile, navigate, setModal]);
    
    const handleSubmitRating = useCallback(async ({ rating, comment }) => {
        if (!tradeInfo || !profile) return;
        const otherUserId = profile.id === tradeInfo.buyerId ? tradeInfo.sellerId : tradeInfo.buyerId;
        const tradeRef = doc(db, "trades", tradeInfo.id);
        const newRatingRef = doc(collection(db, "users", otherUserId, "ratings"));
        try {
            const batch = writeBatch(db);
            batch.set(newRatingRef, {
                rating,
                comment,
                tradeId: tradeInfo.id,
                raterId: profile.id,
                raterUsername: profile.username || 'Anônimo',
                createdAt: serverTimestamp()
            });
            batch.update(tradeRef, { [`ratingsGiven.${profile.id}`]: true });
            await batch.commit();
            setModal({ message: "Avaliação enviada com sucesso! Obrigado." });
        } catch (error) {
            setModal({ message: `Erro ao enviar avaliação: ${error.message}` });
        }
    }, [db, tradeInfo, profile, setModal]);

    const handleDismissRatingPrompt = useCallback(() => {
        if (!profile) return;
        updateTrade({ [`ratingPromptDismissed.${profile.id}`]: true });
    }, [updateTrade, profile]);

    const getUserToRate = useCallback(() => {
        if (!tradeInfo || !profile) return null;
        const otherUserId = profile.id === tradeInfo.buyerId ? tradeInfo.sellerId : tradeInfo.buyerId;
        return tradeInfo.participantInfo?.find(p => p.id === otherUserId) || { username: 'Usuário' };
    }, [tradeInfo, profile]);
    
    return { handleConfirmDelivery, handleConfirmReceipt, handleOpenDispute, handleReclaimCredits, handleOpenChat, handleSubmitRating, handleDismissRatingPrompt, getUserToRate };
};

